/* global VERSION */

import { debounce } from '../utils'
import { DevtoolsBase } from 'function-tree/devtools'
import { Compute } from '../Compute'

const PLACEHOLDER_INITIAL_MODEL = 'PLACEHOLDER_INITIAL_MODEL'
const PLACEHOLDER_DEBUGGING_DATA = '$$DEBUGGING_DATA$$'

/*
  Connects to the Cerebral debugger
  - Triggers events with information from function tree execution
  - Stores data related to time travel, if activated
*/
export class Devtools extends DevtoolsBase {
  constructor({
    storeMutations = true,
    preventExternalMutations = true,
    warnStateProps = true,
    preventPropsReplacement = false,
    bigComponentsWarning = 10,
    host = null,
    https = false,
    reconnect = true,
    reconnectInterval = 5000,
    allowedTypes = [],
    disableDebounce = false,
  } = {}) {
    super({
      host,
      https,
      reconnect,
      reconnectInterval,
    })
    this.version = VERSION
    this.debuggerWatchMap = {}
    this.debuggerComputedMap = {}
    this.storeMutations = storeMutations
    this.preventExternalMutations = preventExternalMutations
    this.warnStateProps = warnStateProps
    this.preventPropsReplacement = preventPropsReplacement
    this.bigComponentsWarning = bigComponentsWarning
    this.nextId = 0

    this.mutations = []
    this.initialModelString = null
    this.controller = null
    this.originalRunTreeFunction = null
    this.isResettingDebugger = false
    this.pendingUpdates = []
    this.computedState = {}
    this.allowedTypes = []
      .concat(typeof File === 'undefined' ? [] : File)
      .concat(typeof FileList === 'undefined' ? [] : FileList)
      .concat(typeof Blob === 'undefined' ? [] : Blob)
      .concat(typeof ImageData === 'undefined' ? [] : ImageData)
      .concat(typeof RegExp === 'undefined' ? [] : RegExp)
      .concat(allowedTypes || [])

    this.sendInitial = this.sendInitial.bind(this)

    if (disableDebounce) {
      return this
    }

    this.sendWatchMapBatch = debounce(this.sendWatchMapBatch, 50)
  }

  createSocket() {
    this.ws = new WebSocket(`${this.https ? 'wss' : 'ws'}://${this.host}`)
  }

  /*
    To remember state Cerebral stores the initial model as stringified
    object. Since the model is mutable this is necessary. The debugger
    passes the execution id of the sequence that was double clicked. This
    execution id is searched backwards in the array of mutations done.
    This is necessary as multiple mutations can be done on the same execution.
    Then all mutations are replayed to the model and all the components
    will be rerendered using the "flush" event and "force" flag.

    It will also replace the "run" method of the controller to
    prevent any new sequences firing off when in "remember state"
  */
  remember(index) {
    this.controller.model.set([], JSON.parse(this.initialModelString))

    if (index === 0) {
      this.controller.run = this.originalRunTreeFunction
    } else {
      this.controller.run = (name) => {
        console.warn(
          `The sequence "${name}" fired while debugger is remembering state, it was ignored`
        )
      }
    }

    for (let x = 0; x < this.mutations.length - index; x++) {
      const mutation = JSON.parse(this.mutations[x].data)
      const method = mutation.method.split('.').pop()

      this.controller.model[method](...mutation.args)
    }

    this.controller.flush(true)
    this.controller.emit(
      'remember',
      JSON.parse(this.mutations[index].data).datetime
    )
  }

  /*

  */
  reset() {
    this.controller.model.set([], JSON.parse(this.initialModelString))
    this.backlog = []
    this.mutations = []
    this.controller.flush(true)
  }

  onMessage(event) {
    const message = JSON.parse(event.data)
    switch (message.type) {
      case 'changeModel':
        this.controller.model.set(message.data.path, message.data.value)
        this.controller.flush()
        break
      case 'remember':
        if (!this.storeMutations) {
          console.warn(
            'Cerebral Devtools - You tried to time travel, but you have turned off storing of mutations'
          )
        } else {
          this.remember(message.data)
        }
        break
      case 'reset':
        this.reset()
        break
      case 'pong':
        this.sendInitial()
        break
      case 'ping':
        this.sendInitial()
        break
    }
  }

  /*
    The debugger might be ready or it might not. The initial communication
    with the debugger requires a "ping" -> "pong" to identify that it
    is ready to receive messages.
    1. Debugger is open when app loads
      - Devtools sends "ping"
      - Debugger sends "pong"
      - Devtools sends "init"
    2. Debugger is opened after app load
      - Debugger sends "ping"
      - Devtools sends "init"
  */
  init(controller) {
    this.controller = controller || this.controller
    this.originalRunTreeFunction = this.controller.run

    if (this.storeMutations) {
      this.initialModelString = JSON.stringify(this.controller.model.get())
    }

    super.init()

    if (controller) {
      this.watchExecution(this.controller, 'c')
    }
  }

  sendReInit() {
    const initialModel = this.controller.model.get()
    const message = JSON.stringify({
      type: 'reinit',
      source: 'c',
      version: this.version,
      data: {
        initialModel,
        initialComputedState: Object.keys(this.computedState).reduce(
          (withValue, path) => {
            withValue[path] = this.computedState[path].getValue()

            return withValue
          },
          {}
        ),
      },
    })

    this.sendMessage(message)
  }

  /*
    Send initial model. If model has already been stringified we reuse it. Any
    backlogged executions will also be triggered
  */
  sendInitial() {
    const initialModel = this.controller.model.get()
    const message = JSON.stringify({
      type: 'init',
      source: 'c',
      version: this.version,
      data: {
        initialModel: this.initialModelString
          ? PLACEHOLDER_INITIAL_MODEL
          : initialModel,
        initialComputedState: Object.keys(this.computedState).reduce(
          (withValue, path) => {
            withValue[path] = this.computedState[path].getValue()

            return withValue
          },
          {}
        ),
      },
    }).replace(`"${PLACEHOLDER_INITIAL_MODEL}"`, this.initialModelString)

    this.isResettingDebugger = true
    this.sendMessage(message)
    if (this.backlog.length) {
      this.sendBulkMessage(this.backlog, 'c')
      this.backlog = []
    }
    this.isResettingDebugger = false

    this.isConnected = true

    this.sendMessage(
      JSON.stringify({
        type: 'watchMap',
        source: 'c',
        version: this.version,
        data: {
          watchMap: this.debuggerWatchMap,
          computedMap: this.debuggerComputedMap,
          updates: [],
        },
      })
    )
  }

  /*
    Create the stringified message for the debugger. As we need to
    store mutations with the default true "storeMutations" option used
    by time travel and jumping between Cerebral apps, we are careful
    not doing unnecessary stringifying.
  */
  createExecutionMessage(debuggingData, execution, functionDetails, payload) {
    const type = 'execution'
    let mutationString = ''

    if (
      this.storeMutations &&
      debuggingData &&
      debuggingData.type === 'mutation'
    ) {
      mutationString = JSON.stringify(debuggingData)
    }

    const data = {
      execution: {
        executionId: execution.id,
        functionIndex: functionDetails.functionIndex,
        payload,
        datetime: execution.datetime,
        data: mutationString ? PLACEHOLDER_DEBUGGING_DATA : debuggingData,
      },
    }

    if (mutationString) {
      this.mutations.push({
        executionId: execution.id,
        data: mutationString,
      })
    }

    return JSON.stringify({
      type,
      source: 'c',
      version: this.version,
      data,
    }).replace(`"${PLACEHOLDER_DEBUGGING_DATA}"`, mutationString)
  }

  updateComputedState(path, value) {
    if (!this.isConnected) {
      return
    }

    this.sendMessage(
      JSON.stringify({
        type: 'computedUpdate',
        source: 'c',
        version: this.version,
        data: {
          path,
          value,
        },
      })
    )
  }

  /*
    Manages computed registered to the state, allowing the devtools to update the
    debugger when values update
  */
  registerComputedState(computed, path) {
    this.computedState[path.join('.')] = computed
  }

  /*
    Updates the map the represents what active state paths and
    components are in your app.Called from Controller. Used by the debugger
  */
  updateWatchMap(watch, nextDeps, prevDeps) {
    if (prevDeps) {
      for (const depsKey in prevDeps) {
        if (nextDeps && depsKey in nextDeps) {
          continue
        }
        const debuggerWatchMapWatchList = this.debuggerWatchMap[depsKey]
        debuggerWatchMapWatchList.splice(
          debuggerWatchMapWatchList.indexOf(watch),
          1
        )
        if (debuggerWatchMapWatchList.length === 0) {
          delete this.debuggerWatchMap[depsKey]
        }
      }
    }

    if (nextDeps) {
      for (const depsKey in nextDeps) {
        if (prevDeps && depsKey in prevDeps) {
          continue
        }
        this.debuggerWatchMap[depsKey] = this.debuggerWatchMap[depsKey]
          ? this.debuggerWatchMap[depsKey].concat(watch)
          : [watch]
      }
    }
  }

  /*
    Updates a map of computed watchers
  */
  updateComputedMap(computed, subscribers) {
    if (computed instanceof Compute) {
      this.debuggerComputedMap[computed.name] = subscribers
        .filter((subscriber) => subscriber.watcher !== computed)
        .map((subscriber) => subscriber.watcher)
    }
  }

  /*
    Sends watch map to debugger. It is debounced (check constructor).
    It needs to wait because React updates async. Instead of tracking
    callbacks we just wait 50ms as it is not that important when
    debugger updates
  */
  sendWatchMap(watchUpdates, changes, start, duration) {
    if (this.isConnected) {
      this.pendingUpdates.push({
        start,
        duration,
        changes,
        watchers: watchUpdates,
      })
      this.sendWatchMapBatch()
    }
  }

  sendWatchMapBatch() {
    this.sendMessage(
      JSON.stringify({
        type: 'watchMap',
        source: 'c',
        version: this.version,
        data: {
          datetime: Date.now(),
          watchMap: this.debuggerWatchMap,
          computedMap: this.debuggerComputedMap,
          updates: this.pendingUpdates,
        },
      })
    )
    this.pendingUpdates = []
  }
}

export default function (...args) {
  return new Devtools(...args)
}
